/* verqo — Design Tier Showcase
   Side-by-side comparison of 3 design directions for the landing page.
*/
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, Sparkles, Check } from "lucide-react";

const TIERS = [
  {
    slug: "landing-v2",
    badge: "Tier 1",
    badgeColor: "bg-slate-200 text-slate-700",
    name: "Keep as-is",
    pitch: "85% modern already. Lime is your signature. Ship & iterate after measuring conversion.",
    palette: ["#FFFFFF", "#0c1612", "#dcff3a"],
    paletteNames: ["Pure white", "Near black", "Lime"],
    changes: [
      "Pure white background",
      "24px radii throughout",
      "Lime liberally applied",
      "Inter Sans throughout",
      "No grain texture",
    ],
    risk: "Looks 90% good but not exceptional",
  },
  {
    slug: "landing-v2-tier2",
    badge: "Tier 2 · LIVE",
    badgeColor: "bg-lime-300 text-slate-900",
    name: "5 surgical upgrades",
    pitch: "Same brand DNA — just tightened to 2026. Warm bg, tighter radii, serif moment, grain texture, custom cursor on hover.",
    palette: ["#FAFAF7", "#0c1612", "#dcff3a"],
    paletteNames: ["Warm off-white", "Near black", "Lime"],
    changes: [
      "Warm off-white background (#FAFAF7)",
      "14px radii (sharper)",
      "Serif italic moments (Instrument Serif)",
      "Subtle grain texture overlay",
      "Lime still surgical, not heavy",
    ],
    risk: "Lower — same DNA, just polished",
    recommended: true,
    live: true,
  },
  {
    slug: "landing-v2-tier3",
    badge: "Tier 3 · Bold",
    badgeColor: "bg-emerald-900 text-emerald-50",
    name: "Forest green reframe",
    pitch: "Genuinely distinctive identity. Deep forest green replaces near-black. Mercury-meets-Linear in our own voice.",
    palette: ["#F8F7F2", "#0c2417", "#dcff3a"],
    paletteNames: ["Cream", "Deep forest", "Lime"],
    changes: [
      "Cream background (#F8F7F2)",
      "Deep forest green primary (#0c2417)",
      "12px radii (sharpest)",
      "Serif italic moments + grain texture",
      "Custom lime+forest cursor",
    ],
    risk: "Higher — distinctive but bigger leap",
  },
];

export default function LandingTiersHub() {
  useEffect(() => { document.title = "verqo · Design tier comparison"; }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" data-testid="tiers-hub">
      <header className="border-b border-slate-200/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/preview/landing-v2" className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-900 transition-colors" data-testid="back-to-current">
              <ArrowLeft className="w-3.5 h-3.5" /> Current landing
            </Link>
            <span className="w-px h-4 bg-slate-200" />
            <span className="text-[12px] font-semibold text-slate-900">verqo · design tiers</span>
          </div>
          <span className="text-[11px] text-slate-400">pick one to ship</span>
        </div>
      </header>

      <section className="max-w-[1280px] mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-12">
        <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 mb-3 inline-flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> 3 strategic directions
        </div>
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tight max-w-3xl leading-tight">
          Which design tier <span className="text-slate-400">should we ship?</span>
        </h1>
        <p className="mt-5 text-[15px] text-slate-600 max-w-2xl leading-relaxed">
          Three live preview routes — each one a fully-styled version of the landing page in a different design direction.
          Click any tier to open it full-size. Compare them side-by-side, then pick one to push to production.
        </p>
      </section>

      <section className="max-w-[1280px] mx-auto px-6 md:px-10 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="tier-grid">
          {TIERS.map((t) => (
            <Link
              key={t.slug}
              to={`/preview/${t.slug}`}
              className={`group relative rounded-2xl border bg-white hover:-translate-y-1 transition-all p-7 flex flex-col ${
                t.recommended
                  ? "border-lime-400 shadow-[0_24px_64px_-24px_rgba(132,204,22,0.35)]"
                  : "border-slate-200 hover:border-slate-300 hover:shadow-lg"
              }`}
              data-testid={`tier-card-${t.slug}`}
            >
              <div className="flex items-center justify-between mb-5">
                <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold ${t.badgeColor}`}>
                  {t.badge}
                </span>
                {t.recommended && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-lime-700">{t.live ? "Shipped · Now /" : "My pick ★"}</span>
                )}
              </div>

              {/* Palette swatches */}
              <div className="flex gap-1.5 mb-5" data-testid={`palette-${t.slug}`}>
                {t.palette.map((c, i) => (
                  <div key={i} className="flex-1">
                    <div
                      className="aspect-[3/2] rounded-lg border border-slate-200"
                      style={{ background: c }}
                    />
                    <div className="text-[9.5px] uppercase tracking-wider text-slate-400 mt-1.5 text-center font-medium">
                      {t.paletteNames[i]}
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-xl font-semibold tracking-tight text-slate-900 leading-snug">{t.name}</div>
              <p className="mt-2.5 text-[13px] leading-relaxed text-slate-600">{t.pitch}</p>

              <div className="mt-5 space-y-1.5 flex-1">
                {t.changes.map((c) => (
                  <div key={c} className="flex items-start gap-1.5 text-[12px] text-slate-700">
                    <Check className="w-3 h-3 text-lime-600 mt-1 flex-shrink-0" />
                    <span>{c}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5 pt-4 border-t border-slate-100 text-[11px] text-slate-500">
                <span className="font-semibold text-slate-700">Risk:</span> {t.risk}
              </div>

              <div className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-lime-700 group-hover:gap-2.5 transition-all">
                Open full preview <ChevronRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-12 text-center text-[12.5px] text-slate-500">
          Tip · open all 3 in separate tabs to compare side-by-side.
        </div>
      </section>
    </div>
  );
}
